import { useEffect, useRef } from 'react';
import { useMutation } from 'convex/react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useLanguage } from '../i18n/useTranslation';
import type { Locale } from '@bill-alps/backend/shared/format/locales';
import { LOCALES } from '@bill-alps/backend/shared/format/locales';

/**
 * Bidirectional locale sync between i18n and the user's DB record.
 * - On login: applies the user's saved locale to i18n.
 * - On language switch (while logged in): persists the new locale to DB.
 */
export function LocaleSync() {
  const { user } = useCurrentUser();
  const { language, changeLanguage } = useLanguage();
  const updateLocale = useMutation(api.features.users.profile.updateLocale);

  // Tracks whether the current language change was initiated by DB sync,
  // so we don't persist it back in a loop.
  const dbSyncRef = useRef(false);
  // Tracks whether we've already applied the DB locale for this user session.
  const appliedRef = useRef(false);

  // On login: apply saved locale from DB
  useEffect(() => {
    if (!user?.locale || appliedRef.current) return;
    if (user.locale !== language) {
      dbSyncRef.current = true;
      changeLanguage(user.locale);
    }
    appliedRef.current = true;
  }, [user?.locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // On language switch: persist to DB (unless it was a DB-initiated change)
  useEffect(() => {
    if (!user || !isLocale(language)) return;

    if (dbSyncRef.current) {
      dbSyncRef.current = false;
      return;
    }

    if (language !== user.locale) {
      updateLocale({ locale: language });
    }
    // `user?.locale` must be a dep: on a rapid A→B language switch, the B run
    // can compare against a stale `user.locale` (A's write not yet delivered)
    // and skip the persist; re-running when the subscription catches up
    // converges the DB onto the locale currently shown in this tab.
  }, [language, user?._id, user?.locale]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}
